import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveImage } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://smapi.sanmartino26.al'}/api`;

const baseLinks = [
  { path: "/", label: "Kreu" },
  { path: "/menu", label: "Menu" },
  { path: "/chef", label: "Chef" },
  { path: "/rezervo", label: "Rezervo" },
  { path: "/kontakt", label: "Kontakt" },
];

export const Navigation = ({ brand }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    axios.get(`${API}/pages`).then((res) => setPages(Array.isArray(res.data) ? res.data : [])).catch(() => { });
  }, []);

  const handleAnchor = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate(`/#${id}`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 200);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePage = (slug) => {
    setOpen(false);
    navigate(`/p/${slug}`);
  };

  // On non-home routes, force scrolled style for legibility (light bg)
  const isScrolled = scrolled || !onHome;

  return (
    <>
      <header
        data-testid="site-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${(isScrolled && !open)
          ? "bg-sm-bg/85 backdrop-blur-md border-b border-sm-border"
          : "bg-transparent"
          }`}
      >
        <div className="sm-container flex items-center justify-between h-16 md:h-20">
          <button
            data-testid="nav-logo"
            onClick={() => {
              setOpen(false);
              onHome ? window.scrollTo({ top: 0, behavior: "smooth" }) : navigate("/");
            }}
            className={`font-serif text-lg md:text-xl tracking-tight transition-colors ${(isScrolled && !open) ? "text-sm-text" : "text-sm-bg"
              }`}
          >
            {(brand?.logo_url || brand?.logo_url_dark) ? (
              <img
                src={resolveImage(((isScrolled && !open) && brand?.logo_url_dark) ? brand.logo_url_dark : (brand?.logo_url || brand?.logo_url_dark))}
              alt="San Martino 26 Logo"
              className="w-auto object-contain h-[var(--logo-mob)] md:h-[var(--logo-desk)] mt-[var(--logo-mt-mob)] md:mt-[var(--logo-mt-desk)]"
              style={{
                "--logo-mob": brand.logo_height_mobile || "32px",
                "--logo-desk": brand.logo_height_desktop || "40px",
                "--logo-mt-mob": brand.logo_margin_top_mobile || "0px",
                "--logo-mt-desk": brand.logo_margin_top_desktop || "0px"
              }}
            />
          ) : (
            <>San Martino <span className="text-sm-terra">26</span></>
          )}
        </button>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {baseLinks.map((l) => (
            <button
              key={l.id || l.path}
              data-testid={`nav-link-${l.id || "menu"}`}
              onClick={() => l.path ? (setOpen(false), navigate(l.path)) : handleAnchor(l.id)}
              className={`sm-link-underline text-[0.78rem] tracking-[0.22em] uppercase font-medium transition-colors ${isScrolled ? "text-sm-text hover:text-sm-olive" : "text-sm-bg hover:text-sm-bg/70"
                }`}
            >
              {l.label}
            </button>
          ))}
          {pages.map((p) => (
            <button
              key={p.slug}
              data-testid={`nav-page-${p.slug}`}
              onClick={() => handlePage(p.slug)}
              className={`sm-link-underline text-[0.78rem] tracking-[0.22em] uppercase font-medium transition-colors ${isScrolled ? "text-sm-text hover:text-sm-olive" : "text-sm-bg hover:text-sm-bg/70"
                }`}
            >
              {p.nav_label}
            </button>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden flex flex-col gap-[5px] p-2 transition-colors ${(isScrolled && !open) ? "text-sm-text" : "text-sm-bg"
            }`}
        >
          <span className={`w-6 h-px bg-current transition-transform ${open ? "translate-y-[6px] rotate-45" : ""}`} />
          <span className={`w-6 h-px bg-current transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`w-6 h-px bg-current transition-transform ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
        </button>
      </div>

      </header>

      {/* Mobile Menu Overlay */}
      <div
        data-testid="nav-mobile-panel"
        className={`md:hidden fixed inset-0 z-40 bg-[#161616]/95 backdrop-blur-sm flex flex-col items-center justify-center transition-all duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col items-center w-full px-6 max-h-screen overflow-y-auto pt-24 pb-12">
          
          {/* Language Toggle */}
          <div className="flex flex-col items-center gap-6 mb-8 text-sm-bg">
            <button className="text-[1.1rem] tracking-[0.3em] uppercase opacity-60 hover:opacity-100 transition-opacity">
              AL
            </button>
            <button className="text-[1.1rem] tracking-[0.3em] uppercase border-b border-sm-bg pb-1">
              EN
            </button>
            <div className="w-1 h-1 rounded-full bg-sm-bg/50 mt-2" />
          </div>

          {/* Links */}
          <div className="flex flex-col items-center gap-7">
            {baseLinks.map((l) => {
              const isActive = location.pathname === l.path || location.hash === `#${l.id}`;
              return (
                <button
                  key={l.id || l.path}
                  data-testid={`nav-mobile-link-${l.id || "menu"}`}
                  onClick={() => l.path ? (setOpen(false), navigate(l.path)) : handleAnchor(l.id)}
                  className={`text-[1.2rem] tracking-[0.35em] uppercase transition-all ${
                    isActive
                      ? "text-sm-bg border-b border-sm-bg pb-1"
                      : "text-sm-bg/60 hover:text-sm-bg"
                  }`}
                >
                  {l.label}
                </button>
              );
            })}
            {pages.map((p) => {
              const isActive = location.pathname === `/p/${p.slug}`;
              return (
                <button
                  key={p.slug}
                  data-testid={`nav-mobile-page-${p.slug}`}
                  onClick={() => handlePage(p.slug)}
                  className={`text-[1.2rem] tracking-[0.35em] uppercase transition-all ${
                    isActive
                      ? "text-sm-bg border-b border-sm-bg pb-1"
                      : "text-sm-bg/60 hover:text-sm-bg"
                  }`}
                >
                  {p.nav_label}
                </button>
              );
            })}
          </div>

        </div>
      </div>
    </>
  );
};

export default Navigation;
